// Car source on consigned cars (Ads Growth Plan A5): how the consignor found
// Skyway. Stored on cars/{stock} as a stable lowercase key so reports can group
// cost per consigned car by channel.
//
// Fields written by the Consignor tab (see buildCarSourceUpdate):
//   car_source            one of CAR_SOURCE_KEYS
//   car_source_auto       true when staff kept the suggestion unchanged
//   car_source_origin     where the value came from (CAR_SOURCE_ORIGINS)
//   car_source_detail     the evidence behind a suggestion, e.g. 'gclid'
//   car_source_lead_id    the lead the suggestion came from, if any
//   car_source_suggested  the suggested key at save time, or null
//   car_source_overridden true when staff picked something other than the suggestion
//   car_source_set_by / car_source_set_by_name / car_source_set_at

export const CAR_SOURCES = [
  { key: 'google', label: 'Google' },
  { key: 'meta', label: 'Meta' },
  { key: 'referral', label: 'Referral' },
  { key: 'repeat', label: 'Repeat' },
  { key: 'walk_in', label: 'Walk-in' },
  { key: 'dealer', label: 'Dealer' },
];

export const CAR_SOURCE_KEYS = CAR_SOURCES.map(s => s.key);

export const CAR_SOURCE_ORIGINS = {
  LEAD_FIRST_TOUCH: 'lead_first_touch',
  LEAD_SOURCE: 'lead_source',
  DEALER_CONSIGNOR: 'dealer_consignor',
  CUSTOMER_HISTORY: 'customer_history',
  MANUAL: 'manual',
};

export const UNKNOWN_CAR_SOURCE_LABEL = 'Unknown — please set';

// Consignors known to be dealers. Customer docs carry no dealer flag today, so
// this list plus the optional flags in isDealerConsignor is the whole signal.
// Ron's Classic Cars — the same id the checkeeper Ron check-in flow writes.
export const DEALER_CONSIGNOR_IDS = ['4ed13115-1900-41d2-a14d-13f1242dd48a'];

export const isValidCarSource = (key) => CAR_SOURCE_KEYS.includes(key);

export const carSourceLabel = (key) =>
  (CAR_SOURCES.find(s => s.key === key) || {}).label || UNKNOWN_CAR_SOURCE_LABEL;

// The source is required until the consignment agreement is signed. A car that
// already has a signed agreement is past intake, so a legacy car without a source
// never blocks; it shows UNKNOWN_CAR_SOURCE_LABEL instead.
export const carSourceRequired = (car = {}) => !!car.consignor && !car.consignment_doc;

// Returns an error message, or null when the car passes.
export const validateCarSource = (car = {}) => {
  if (!carSourceRequired(car)) return null;
  if (!isValidCarSource(car.car_source)) return 'Missing car source';
  return null;
};

const str = (v) => (typeof v === 'string' ? v.trim().toLowerCase() : '');
const present = (v) => str(v) !== '';

const GOOGLE_TOKENS = ['google', 'adwords', 'gads', 'youtube'];
const META_TOKENS = ['facebook', 'fb', 'instagram', 'ig', 'meta'];
const SEARCH_HOSTS = ['bing.', 'yahoo.', 'duckduckgo.', 'ecosia.'];
const OWN_HOSTS = ['skywayclassics.com'];

const hostOf = (referrer) => {
  const raw = str(referrer);
  if (!raw) return '';
  try {
    return new URL(raw.includes('://') ? raw : `https://${raw}`).hostname.replace(/^www\./, '');
  } catch (e) {
    return '';
  }
};

const hostIsGoogle = (host) => /(^|\.)google\.[a-z.]+$/.test(host) || host.endsWith('youtube.com');
const hostIsMeta = (host) => ['facebook.com', 'fb.com', 'instagram.com', 'messenger.com'].some(h => host === h || host.endsWith(`.${h}`));

// Maps one lead's first-touch attribution to a car source.
// Field names match skyway-functions integrations/leads/helpers.js (PR #176):
// gclid/wbraid/gbraid, fbclid/fbc, utm_source/utm_medium, referrer, traffic_source.
// Returns { key, detail } or null.
export const carSourceFromAttribution = (lead = {}) => {
  if (!lead || typeof lead !== 'object') return null;

  if (['gclid', 'wbraid', 'gbraid'].some(k => present(lead[k]))) {
    return { key: 'google', detail: ['gclid', 'wbraid', 'gbraid'].find(k => present(lead[k])) };
  }
  if (present(lead.fbclid) || present(lead.fbc)) {
    return { key: 'meta', detail: present(lead.fbclid) ? 'fbclid' : 'fbc' };
  }

  const source = str(lead.utm_source);
  const medium = str(lead.utm_medium);
  if (source) {
    if (GOOGLE_TOKENS.includes(source)) return { key: 'google', detail: `utm_source=${source}` };
    if (META_TOKENS.includes(source)) return { key: 'meta', detail: `utm_source=${source}` };
  }
  if (medium === 'referral') return { key: 'referral', detail: 'utm_medium=referral' };

  const traffic = str(lead.traffic_source);
  if (traffic) {
    if (traffic === 'google_ads' || traffic.startsWith('google_')) return { key: 'google', detail: `traffic_source=${traffic}` };
    if (traffic === 'meta_ads' || META_TOKENS.some(t => traffic === t || traffic.startsWith(`${t}_`))) {
      return { key: 'meta', detail: `traffic_source=${traffic}` };
    }
  }

  const host = hostOf(lead.referrer);
  if (host) {
    if (hostIsGoogle(host)) return { key: 'google', detail: `referrer=${host}` };
    if (hostIsMeta(host)) return { key: 'meta', detail: `referrer=${host}` };
    // Another site linked to us. Other search engines and our own pages are not referrals.
    if (!SEARCH_HOSTS.some(h => host.startsWith(h) || host.includes(`.${h}`))
      && !OWN_HOSTS.some(h => host === h || host.endsWith(`.${h}`))) {
      return { key: 'referral', detail: `referrer=${host}` };
    }
  }
  return null;
};

// Maps the free-text lead.source ("Walk-in", "Referral", "Facebook", ...) to a car source.
export const carSourceFromLeadSourceText = (text) => {
  const s = str(text);
  if (!s) return null;
  if (/walk[\s-_]?in/.test(s)) return 'walk_in';
  if (/referr/.test(s)) return 'referral';
  if (/dealer/.test(s)) return 'dealer';
  if (/repeat|returning/.test(s)) return 'repeat';
  if (/google|adwords|youtube/.test(s)) return 'google';
  if (/facebook|instagram|\bmeta\b|\bfb\b|\big\b/.test(s)) return 'meta';
  return null;
};

// Other cars a customer consigned or bought, given store.getCustomerCarStocks.
// Skips the current car and service (-SR) stocks, which are not sales or consignments.
export const countPriorCarStocks = (stocks = [], currentStock) =>
  new Set((Array.isArray(stocks) ? stocks : [])
    .filter(s => s && s !== currentStock && !String(s).endsWith('-SR'))).size;

export const isDealerConsignor = (consignor = {}) => {
  if (!consignor || typeof consignor !== 'object') return false;
  if (DEALER_CONSIGNOR_IDS.includes(consignor.id)) return true;
  return consignor.is_dealer === true
    || str(consignor.customer_type) === 'dealer'
    || str(consignor.type) === 'dealer';
};

const leadTime = (lead) => {
  const raw = lead.attribution_captured_at || lead.sortDate || lead.date || lead.created_at || '';
  const t = Date.parse(String(raw).replace(/\//g, '-'));
  return Number.isFinite(t) ? t : Infinity;
};

/**
 * Suggests a car source for a consigned car. Pure — the caller fetches the inputs.
 *
 * Priority:
 *   1. dealer consignor
 *   2. repeat — the consignor already consigned or bought another car. Their
 *      first lead belongs to that earlier car, so it must not be counted twice.
 *   3. first-touch attribution on the consignor's oldest attributed lead
 *   4. the free-text source on the consignor's oldest lead that maps to one
 *
 * @param {object} input
 * @param {object} input.consignor        customer doc (with id)
 * @param {object[]} input.leads          the consignor's leads, any order
 * @param {number} input.priorCarCount    other cars this customer consigned or bought
 * @returns {null|{key, origin, detail, leadId}}
 */
export const suggestCarSource = ({ consignor = {}, leads = [], priorCarCount = 0 } = {}) => {
  if (isDealerConsignor(consignor)) {
    return { key: 'dealer', origin: CAR_SOURCE_ORIGINS.DEALER_CONSIGNOR, detail: 'dealer consignor', leadId: null };
  }
  if (priorCarCount > 0) {
    return { key: 'repeat', origin: CAR_SOURCE_ORIGINS.CUSTOMER_HISTORY, detail: `${priorCarCount} earlier car(s)`, leadId: null };
  }

  const ordered = (Array.isArray(leads) ? leads : [])
    .filter(l => l && typeof l === 'object')
    .slice()
    .sort((a, b) => leadTime(a) - leadTime(b));

  for (const lead of ordered) {
    const hit = carSourceFromAttribution(lead);
    if (hit) return { key: hit.key, origin: CAR_SOURCE_ORIGINS.LEAD_FIRST_TOUCH, detail: hit.detail, leadId: lead.id || null };
  }
  for (const lead of ordered) {
    const key = carSourceFromLeadSourceText(lead.source);
    if (key) return { key, origin: CAR_SOURCE_ORIGINS.LEAD_SOURCE, detail: `source=${lead.source}`, leadId: lead.id || null };
  }
  return null;
};

/**
 * Builds the cars/{stock} merge update for a staff choice.
 * `suggestion` is what suggestCarSource returned when staff chose, or null.
 */
export const buildCarSourceUpdate = ({ key, suggestion = null, user = {}, now = new Date() }) => {
  if (!isValidCarSource(key)) throw new Error(`Invalid car source: ${key}`);
  const kept = !!suggestion && suggestion.key === key;
  return {
    car_source: key,
    car_source_auto: kept,
    car_source_origin: kept ? suggestion.origin : CAR_SOURCE_ORIGINS.MANUAL,
    car_source_detail: kept ? (suggestion.detail || null) : null,
    car_source_lead_id: kept ? (suggestion.leadId || null) : null,
    car_source_suggested: suggestion ? suggestion.key : null,
    car_source_overridden: !!suggestion && !kept,
    car_source_set_by: user.id || null,
    car_source_set_by_name: user.name || null,
    car_source_set_at: now.toISOString(),
  };
};

// Counts cars per source for the Consignment Dashboard. Cars without a valid
// source land under 'unknown'. Every key is present, so zero counts show too.
export const countCarsBySource = (cars = []) => {
  const counts = CAR_SOURCE_KEYS.reduce((acc, k) => ({ ...acc, [k]: 0 }), { unknown: 0 });
  (Array.isArray(cars) ? cars : []).forEach(car => {
    const key = car && isValidCarSource(car.car_source) ? car.car_source : 'unknown';
    counts[key] += 1;
  });
  return counts;
};
